import { AppLayout } from "@/components/layout";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { apiFetch } from "@/lib/api";
import { Users, Search, Shield, UserCog, LogIn } from "lucide-react";
import { useState, useEffect } from "react";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import { format } from "date-fns";

type UserItem = {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
  gymId: string | null;
  createdAt: string;
};

export default function SuperadminUsers() {
  const { toast } = useToast();
  const [users, setUsers] = useState<UserItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState<string>("ALL");

  useEffect(() => {
    apiFetch("/superadmin/users")
      .then(setUsers)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  async function impersonate(userId: string) {
    try {
      const result = await apiFetch<{ token?: string }>(`/superadmin/impersonate/${userId}`, { method: "POST" });
      if (result.token) {
        localStorage.setItem("impersonating", "true");
        localStorage.setItem("originalToken", localStorage.getItem("token") || "");
        localStorage.setItem("token", result.token);
        window.location.href = "/";
      }
    } catch (e: any) {
      toast({ title: "Error", description: e.message, variant: "destructive" });
    }
  }

  const filtered = users.filter((u) => {
    const matchSearch = `${u.firstName} ${u.lastName} ${u.email}`.toLowerCase().includes(search.toLowerCase());
    const matchRole = roleFilter === "ALL" || u.role === roleFilter;
    return matchSearch && matchRole;
  });

  const roles = ["ALL", "SUPERADMIN", "ADMIN", "TRAINER", "STUDENT"];
  const roleColors: Record<string, string> = {
    SUPERADMIN: "bg-purple-500/10 text-purple-400",
    ADMIN: "bg-blue-500/10 text-blue-400",
    TRAINER: "bg-primary/10 text-primary",
    STUDENT: "bg-green-500/10 text-green-400",
  };

  return (
    <AppLayout>
      <div className="flex flex-col gap-8 pb-12">
        <div>
          <h1 className="text-4xl font-display font-bold text-white mb-2">Platform Users</h1>
          <p className="text-muted-foreground">{users.length} total users</p>
        </div>

        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input placeholder="Search users..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-10 bg-background border-white/10" />
          </div>
          <div className="flex gap-2">
            {roles.map((r) => (
              <button key={r} onClick={() => setRoleFilter(r)} className={cn(
                "px-3 py-2 rounded-lg text-xs font-semibold uppercase tracking-wider transition-all",
                roleFilter === r ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-white/5"
              )}>
                {r}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="space-y-4">{[1, 2, 3, 4].map((i) => <div key={i} className="h-16 bg-white/5 rounded-xl animate-pulse" />)}</div>
        ) : filtered.length === 0 ? (
          <div className="p-12 text-center rounded-2xl border border-dashed border-white/10 bg-white/5">
            <Users className="w-12 h-12 text-muted-foreground mx-auto mb-4 opacity-50" />
            <p className="text-white font-medium text-lg">No users found</p>
          </div>
        ) : (
          <div className="space-y-3">
            {filtered.map((u) => (
              <Card key={u.id} className="glass-panel border-white/5">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center text-sm font-bold text-primary">
                        {u.firstName[0]}{u.lastName[0]}
                      </div>
                      <div>
                        <p className="font-bold text-white">{u.firstName} {u.lastName}</p>
                        <p className="text-xs text-muted-foreground">{u.email}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className={cn("text-xs px-2 py-0.5 rounded-full font-semibold uppercase", roleColors[u.role] || "bg-white/10 text-muted-foreground")}>
                        {u.role}
                      </span>
                      {u.role !== "SUPERADMIN" && (
                        <Button size="sm" variant="ghost" onClick={() => impersonate(u.id)} className="text-muted-foreground hover:text-white" title="Impersonate user">
                          <LogIn className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </AppLayout>
  );
}
